import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createGallery } from "@/lib/api";
import PortalNotesFields from "@/components/PortalNotesFields";
import GalleryView from "@/components/previews/GalleryView";
import MediaUploadButton from "@/components/MediaUploadButton";

export default function GalleryBuilder() {
  const nav = useNavigate();
  const [title, setTitle] = useState("Campus photos");
  const [description, setDescription] = useState("");
  const [layout, setLayout] = useState("grid");
  const [items, setItems] = useState([]);
  const [mediaMap, setMediaMap] = useState({});
  const [portals, setPortals] = useState([]);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const preview = useMemo(() => ({ title, description, layout, items }), [title, description, layout, items]);

  const onUploaded = (m) => {
    setMediaMap((prev) => ({ ...prev, [m.id]: m }));
    setItems((prev) => [...prev, { media_id: m.id, caption: "", link: "" }]);
  };
  const removeItem = (i) => setItems((l) => l.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
  };
  const updateField = (i, field, value) =>
    setItems((l) => l.map((it, idx) => idx === i ? { ...it, [field]: value } : it));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!items.length) { toast.error("Add at least one photo"); return; }
    setSubmitting(true);
    try {
      const res = await createGallery({
        title, description, layout, items, portals, notes, tags, folder_id: folderId,
      });
      toast.success("Gallery created");
      nav(`/success/gallery/${res.id}`);
    } catch {
      toast.error("Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">New Component</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
            Build a Photo Gallery
          </h1>
          <p className="text-zinc-600 mt-2">
            Upload photos, pick a layout, and get a single embeddable URL.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <form data-testid="builder-form" onSubmit={onSubmit} className="md:col-span-7 space-y-8">
            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Heading</div>
              <div>
                <Label className="text-xs text-zinc-500">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                  data-testid="gallery-title-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Description (optional)</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  rows={2} className="mt-1.5 resize-none" data-testid="gallery-description-input" />
              </div>
            </section>

            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Layout</div>
              <div className="grid grid-cols-2 gap-3">
                {["grid", "slider"].map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLayout(l)}
                    data-testid={`layout-${l}`}
                    className={`p-4 rounded-md border text-left transition ${
                      layout === l ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <div className="text-sm font-medium capitalize text-zinc-950">{l}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {l === "grid" ? "Masonry grid · click for lightbox" : "One photo at a time · arrows + dots"}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Photos</div>
                  <p className="text-xs text-zinc-500 mt-1">Up to 10 MB each · JPEG / PNG / WebP / GIF</p>
                </div>
                <MediaUploadButton onUploaded={onUploaded} testid="gallery-upload-btn" />
              </div>

              <div className="space-y-3">
                {items.map((it, i) => {
                  const m = mediaMap[it.media_id];
                  return (
                    <div key={i} className="flex gap-3 items-start p-3 rounded-md border border-zinc-200 bg-[#FAFAFA]"
                         data-testid={`gallery-item-${i}`}>
                      <div className="text-zinc-300"><GripVertical className="h-4 w-4" /></div>
                      <div className="h-20 w-28 rounded overflow-hidden bg-zinc-100 flex-shrink-0">
                        {m?.url ? <img src={m.url} alt="" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <Input value={it.caption}
                          onChange={(e) => updateField(i, "caption", e.target.value)}
                          placeholder="Caption (optional)"
                          className="h-8 text-sm"
                          data-testid={`gallery-caption-${i}`} />
                        <Input value={it.link}
                          onChange={(e) => updateField(i, "link", e.target.value)}
                          placeholder="Link (optional)"
                          className="h-8 text-sm font-mono"
                          data-testid={`gallery-link-${i}`} />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                                className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 disabled:opacity-30"
                                title="Move up" data-testid={`gallery-up-${i}`}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                                className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 disabled:opacity-30"
                                title="Move down" data-testid={`gallery-down-${i}`}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => removeItem(i)}
                                className="h-7 w-7 grid place-items-center rounded text-zinc-400 hover:text-red-600 hover:bg-red-50"
                                title="Remove" data-testid={`gallery-remove-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!items.length ? (
                  <p className="text-xs text-zinc-400 text-center py-6 border border-dashed border-zinc-200 rounded-md">
                    No photos yet. Click <strong>Upload images</strong> above.
                  </p>
                ) : null}
              </div>
            </section>

            <PortalNotesFields portals={portals} setPortals={setPortals}
                              notes={notes} setNotes={setNotes}
                              tags={tags} setTags={setTags}
                              folderId={folderId} setFolderId={setFolderId} />

            <Button type="submit" data-testid="generate-embed-btn" disabled={submitting}
              className="h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Generate embed URL
            </Button>
          </form>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Live Preview</div>
              <div data-testid="preview-container" className="border border-zinc-200 rounded-md bg-white overflow-hidden">
                <div className="border-b border-zinc-100 px-4 py-2 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="ml-3 text-[11px] font-mono text-zinc-400">embed/gallery/preview</span>
                </div>
                <div className="p-6 bg-white min-h-[480px]">
                  <GalleryView data={preview} mediaMap={mediaMap} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
